/* eslint-disable no-console */
/* eslint-disable no-return-assign */

import { Machine } from 'xstate';
import Yup from 'yup';
import axios from 'axios';
import * as Sentry from '@sentry/browser';
import {
    defaultLinkState,
    markActive,
    markInactive,
    markEditorNotDirty,
    markPostSuccess,
    resetEditor,
    disableLinkPreview,
    togglePostAnonymity,
    syncEditorUpdates,
    setError,
    saveEmbedlyData,
    setLinkToDefault,
    selectPostGroup,
    clearError,
    setImageUrl,
    setImageToDefault,
    setUploadError,
    clearUploadError,
} from './actions';

import { editorIsInactive, editorNotDirty, publishingAllowed } from './guards';
import { CommunityUnifiedPostYupSchema } from '../../../Includes/FormSchema/ContentSchema';
import { EmitMetric } from '../../../Analytics/VisibilityPixel/VisibilityPixel';
import { parsePost } from '../utils';

const eventTypes = {
    OPEN: 'TELEMETRY_OPEN',
    CLOSE: 'TELEMETRY_CLOSE',
    ACTIVE: 'TELEMETRY_ACTIVE',
    POST: 'TELEMETRY_POST',
    PUBLISH: 'TELEMETRY_PUBLISH',
    UPDATE: 'TELEMETRY_UPDATE',
    CANCEL: 'TELEMETRY_CANCEL',
    DEFAULT: 'TELEMETRY_DEFAULT',
};

const determinePostType = (isPhoto = false, isLink = false) => {
    if (isPhoto) return 'Photo';
    if (isLink) return 'Link';
    return 'Discussion';
};

const sendTelemetry = (context, event) => {
    const telemetryMessage = eventTypes[event.type] || eventTypes.DEFAULT;

    console.log(`SENDING TELEMETRY: ${telemetryMessage}`);
};

const validateForm = (fieldsToValidate, formSchema) =>
    Yup.object()
        .shape(formSchema)
        .validate(fieldsToValidate, { abortEarly: false });

const flattenApiErrors = (errors = {}) => {
    if (Array.isArray(errors)) return errors;

    return Object.values(errors).reduce((messages, error) => {
        if (Array.isArray(error)) return messages.concat(error);
        if (typeof error === 'string') return messages.concat(error);
        return messages;
    }, []);
};

const getApiErrorMessages = (error) =>
    flattenApiErrors(error?.response?.data?.errors);

const publishPostToFeed = async (context) => {
    const {
        postText,
        link,
        image,
        isAnonymousPost,
        communityGroupHash,
        html,
        hashtags = [],
        communityTopicId,
        updateCommunityContext,
        sortTypeRef,
    } = context;

    const isLink = link && link.url;
    const isPhoto = image && image.length;
    const postFlagType = determinePostType(isPhoto, isLink);

    try {
        const { title, body } = parsePost(postText);
        const postContent = {
            title,
            body,
            rawHtml: html,
            image,
            link,
            isAnon: isAnonymousPost,
            communityGroupHash,
            hashtags,
            ...(communityGroupHash
                ? { communityTopicId: null }
                : { communityTopicId }),
        };

        const isValid = await validateForm(
            postContent,
            CommunityUnifiedPostYupSchema
        );

        if (!isValid) return;

        const response = await axios.post(
            `/api/community/content/${postFlagType}`,
            postContent
        );

        const { hash } = response.data.data;
        const postMetricObject = {
            content_created: 1,
            community_content_key: `community-${postFlagType.toLowerCase()}-${hash}`,
            ...(communityGroupHash
                ? { community_group_hash: communityGroupHash }
                : {}),
            ...(isPhoto ? { cloudinaryId: image } : {}),
        };

        updateCommunityContext(response.data.data);
        if (sortTypeRef?.current && !communityGroupHash) {
            postMetricObject.community_sort_type = sortTypeRef.current?.toLowerCase();
        }
        EmitMetric(postMetricObject);
        return response;
    } catch (error) {
        const errors = getApiErrorMessages(error);
        Sentry.captureException(
            new Error('Failed to publish post from unified post box'),
            {
                extra: {
                    tags: {
                        project: 'Unified Post Box',
                        contentType: postFlagType,
                    },
                    exception: error.message,
                },
            }
        );
        const publishError = new Error('Failed to publish post');
        publishError.errors =
            errors.length > 0
                ? errors
                : ['Something went wrong. Please try your post again.'];
        throw publishError;
    }
};

const updatePostToFeed = async (context) => {
    const {
        postText,
        link,
        image,
        isAnonymousPost,
        communityGroupHash,
        html,
        hashtags = [],
        communityTopicId,
        editCommunityContent,
        contentType,
        hash,
        isComment,
        sortTypeRef,
    } = context;

    const isLink = link && link.url;
    const isPhoto = image && image.length;
    const postFlagType = determinePostType(isPhoto, isLink);

    try {
        const { title, body } = parsePost(postText);
        let trueBody = body === '' ? null : body;
        let truetitle = title;
        let isPhotoPost = false;
        if (contentType === 'community-link') {
            if (body !== '') trueBody = `${title}\n${body}`;
            else trueBody = `${title}`;
            truetitle = null;
        }
        if (contentType === 'community-photo') {
            trueBody = null;
            if (body !== '') truetitle = `${title}\n${body}`;
            else truetitle = `${title}`;
            isPhotoPost = true;
        }
        const postContent = {
            title: truetitle,
            rawHtml: html,
            description: trueBody,
            isAnon: isAnonymousPost,
            anon: isAnonymousPost,
            communityGroupHash,
            hashtags,
            ...(communityGroupHash
                ? { communityTopicId: null }
                : { communityTopicId }),
        };
        if (isComment) postContent.comment = `${title}\n${body}`;

        const response = await editCommunityContent(
            contentType,
            postContent,
            hash,
            isPhotoPost
        );
        const postMetricObject = {
            content_edited: 1,
            community_content_key: `community-${postFlagType.toLowerCase()}-${hash}`,
            ...(communityGroupHash
                ? { community_group_hash: communityGroupHash }
                : {}),
            ...(isPhoto ? { cloudinaryId: image } : {}),
        };
        if (sortTypeRef?.current && !communityGroupHash) {
            postMetricObject.community_sort_type = sortTypeRef.current?.toLowerCase();
        }
        EmitMetric(postMetricObject);
        return response;
    } catch (error) {
        const errors = getApiErrorMessages(error);
        console.log(error.message);
        Sentry.captureException(
            new Error('Failed to update post from unified post box'),
            {
                extra: {
                    tags: {
                        project: 'Unified Post Box',
                        contentType: postFlagType,
                    },
                    exception: error.message,
                },
            }
        );
        const updateError = new Error('Failed to update post');
        updateError.errors =
            errors.length > 0
                ? errors
                : ['Something went wrong. Please try your post again.'];
        throw updateError;
    }
};

const postBoxMachine = Machine(
    {
        id: 'postbox',
        initial: 'closed', // depends on mode (create/ edit)
        context: {
            mode: 'create', // create or edit
            isDirty: false, // when there is a change
            isInactive: true,
            postText: '', // initialize in component in useMachine - withContext(html)
            html: '',
            isAnonymousPost: false,
            link: defaultLinkState,
            hashtags: [],
            image: null, // cloudinary img src
            isPreviewDisabled: false, // dont show preview (link or photo)
            error: [],
            type: 'Discussion', // Discussion, Link, Photo
            postSuccess: false,
            communityGroupHash: '',
            communityTopicId: null,
            uploadError: [],
        },
        states: {
            closed: {
                entry: ['logger'],
                on: {
                    OPEN: {
                        target: 'open',
                        actions: ['clearError'],
                    },
                    CLEAR_ERROR: {
                        actions: ['clearError'],
                    },
                    SELECT_POST_GROUP: {
                        actions: ['selectPostGroup'],
                    },
                },
            },
            open: {
                entry: [],
                on: {
                    CLOSE: [
                        {
                            cond: 'editorNotDirty',
                            target: 'closed',
                        },
                    ],
                    ACTIVE: {
                        actions: ['markActive'],
                    },
                    EDITOR_CLEAN: {
                        actions: ['markEditorNotDirty', 'setLinkToDefault'],
                    },
                    INACTIVE: {
                        actions: ['resetEditor'],
                    },
                    DISABLE_PREVIEW: {
                        actions: ['disableLinkPreview'],
                    },
                    SAVE_EMBEDLY: {
                        actions: ['saveEmbedlyData'],
                    },
                    SET_LINK_TO_DEFAULT: {
                        actions: ['setLinkToDefault'],
                    },
                    EDITOR_UPDATE: {
                        actions: ['syncEditorUpdates'],
                    },
                    TOGGLE_POST_ANONYMITY: {
                        actions: ['togglePostAnonymity'],
                    },
                    PUBLISH: {
                        cond: 'publishingAllowed',
                        target: 'publish',
                    },
                    UPDATE: {
                        cond: 'publishingAllowed',
                        target: 'update',
                    },
                    SELECT_POST_GROUP: {
                        actions: ['selectPostGroup'],
                    },
                    SET_IMAGE_URL: {
                        actions: ['setImageUrl'],
                    },
                    SET_IMAGE_TO_DEFAULT: {
                        actions: ['setImageToDefault'],
                    },
                    SET_PHOTO_UPLOAD_ERROR: {
                        actions: ['setUploadError'],
                    },
                    CLEAR_PHOTO_UPLOAD_ERROR: {
                        actions: ['clearUploadError'],
                    },
                },
            },
            publish: {
                entry: ['logger'],
                invoke: {
                    id: 'publishPostToFeed',
                    src: publishPostToFeed,
                    onDone: {
                        target: 'success',
                    },
                    onError: {
                        target: 'open',
                        actions: ['setError'],
                    },
                },
            },
            update: {
                entry: ['logger'],
                invoke: {
                    id: 'updatePostToFeed',
                    src: updatePostToFeed,
                    onDone: {
                        target: 'success',
                    },
                    onError: {
                        target: 'open',
                        actions: ['setError'],
                    },
                },
            },
            cancel: {
                entry: ['logger'],
                on: {
                    OPEN: 'open',
                    SELECT_POST_GROUP: {
                        actions: ['selectPostGroup'],
                    },
                },
            },
            success: {
                entry: ['logger', 'markPostSuccess'],
                exit: ['resetEditor'],
                after: { 150: 'closed' },
            },
            error: {
                entry: ['logger', 'setError'],
                on: {
                    CLOSE: [
                        {
                            cond: 'editorIsInactive',
                            target: 'closed',
                        },
                    ],
                    PUBLISH: [
                        {
                            cond: 'publishingAllowed',
                            target: 'publish',
                        },
                    ],
                    UPDATE: [
                        {
                            cond: 'publishingAllowed',
                            target: 'update',
                        },
                    ],
                    INACTIVE: {
                        actions: ['resetEditor'], // TODO could fire telemetry here, probably would want to debounce in some fashion
                    },
                    EDITOR_UPDATE: {
                        actions: ['syncEditorUpdates'],
                    },
                    CLEAR_ERROR: {
                        actions: ['clearError'],
                    },
                },
            },
        },
    },
    {
        actions: {
            markActive,
            markInactive,
            markEditorNotDirty,
            resetEditor,
            syncEditorUpdates,
            disableLinkPreview,
            markPostSuccess,
            setError,
            sendTelemetry,
            logger: (context) => {
                // log machine transitions
                if (process.env.NODE_ENV !== 'production') {
                    console.table({ machineState: context });
                }
            },
            togglePostAnonymity,
            saveEmbedlyData,
            setLinkToDefault,
            selectPostGroup,
            clearError,
            setImageUrl,
            setImageToDefault,
            setUploadError,
            clearUploadError,
        },
        guards: {
            editorIsInactive,
            editorNotDirty,
            publishingAllowed,
        },
    }
);

export default postBoxMachine;
